import { localeFromPath, routeFromPath } from "../lib/router";
import type { Locale } from "../lib/i18n";

export interface PageSnapshot {
  version: 1;
  markup: string;
  head: string;
  locale: Locale;
  route: string;
  path: string;
  title: string;
  digest: string;
}

export function snapshotUrl(pathname: string) {
  const route = routeFromPath(pathname);
  if (route === "notFound") throw new Error("Unsupported client route");
  const name = route.startsWith("article/") ? route.slice(8) : route;
  if (!/^[a-z0-9-]+$/i.test(name)) throw new Error("Invalid snapshot name");
  return `${import.meta.env.BASE_URL}assets/pages/${localeFromPath(pathname)}/${name}.json`;
}

// A narrowly scoped Trusted Types policy: only the verified snapshot currently
// being installed can pass, not arbitrary caller strings or URL parameters.
type HtmlPolicy = { createHTML: (input: string) => unknown };
const trustedTypes = (window as Window & {
  trustedTypes?: { createPolicy: (name: string, rules: { createHTML: (input: string) => string }) => HtmlPolicy };
}).trustedTypes;
let approvedHtml: string | null = null;
const policy = trustedTypes?.createPolicy("parapendio-pages", {
  createHTML: (input) => {
    if (input !== approvedHtml) throw new TypeError("Unverified page HTML");
    return input;
  },
});

export async function readSnapshot(pathname: string): Promise<PageSnapshot> {
  const response = await fetch(snapshotUrl(pathname), { cache: "no-cache" });
  if (!response.ok) throw new Error(`Page snapshot HTTP ${response.status}`);
  const page: PageSnapshot = await response.json();
  if (page.version !== 1 || page.locale !== localeFromPath(pathname) ||
      page.route !== routeFromPath(pathname) || typeof page.markup !== "string" ||
      typeof page.head !== "string") throw new Error("Invalid page snapshot");
  const { digest, ...payload } = page;
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(payload)));
  const actual = Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join("");
  if (actual !== digest) throw new Error("Page snapshot integrity mismatch");
  return page;
}

export function installSnapshot(root: HTMLElement, page: PageSnapshot) {
  approvedHtml = `<html><head>${page.head}</head><body>${page.markup}</body></html>`;
  let parsed: Document;
  try {
    const html = policy ? policy.createHTML(approvedHtml) : approvedHtml;
    parsed = new DOMParser().parseFromString(html as string, "text/html");
  } finally { approvedHtml = null; }
  root.replaceChildren(...Array.from(parsed.body.childNodes));
  // Keep CSP/nonces, font preloads and the original tracking script untouched.
  const selector = 'title,meta[name="description"],meta[name="keywords"],meta[name="robots"],meta[property^="og:"],meta[name^="twitter:"],link[rel="canonical"],link[rel="alternate"][hreflang],script[type="application/ld+json"]';
  document.head.querySelectorAll(selector).forEach((node) => node.remove());
  for (const node of Array.from(parsed.head.querySelectorAll(selector))) document.head.append(node);
  document.documentElement.lang = page.locale;
}
